// Det stiliserede Danmark: knudepunkter, kystlinje og udlandsnoder.
// Koordinater er lat/lon, projiceret til et logisk kort i km: x mod øst, y mod syd.
// Tallene er spilværdier til et spil, ikke Energinets officielle data.

import type { Area } from '../sim/types';

export const LON0 = 8.0;
export const LAT0 = 57.8;
/** km pr. længdegrad ved 56° N. */
export const KM_PER_LON = 62.25;
export const KM_PER_LAT = 110.57;

export function project(lat: number, lon: number): { x: number; y: number } {
  return {
    x: Math.round((lon - LON0) * KM_PER_LON * 10) / 10,
    y: Math.round((LAT0 - lat) * KM_PER_LAT * 10) / 10,
  };
}

export interface MapNode {
  id: string;
  name: string;
  lat: number;
  lon: number;
  x: number;
  y: number;
  area: Area;
  /** Kystnode: må få søkabel til havvind. */
  coast: boolean;
  /** Havvind eller udland ligger på vandet. */
  offshore?: boolean;
  foreign?: boolean;
  /** Udland: grøn andel (Norge og Sverige 1, Tyskland 0,5). */
  green?: number;
}

type Raw = [id: string, name: string, lat: number, lon: number, area: Area, coast: boolean, extra?: Partial<MapNode>];

const RAW: Raw[] = [
  // Nordjylland
  ['aalborg', 'Aalborg', 57.05, 9.92, 'DK1', true],
  ['ferslev', 'Ferslev', 56.94, 9.98, 'DK1', false],
  ['nordjyllandsvaerket', 'Nordjyllandsværket', 57.07, 10.03, 'DK1', true],
  ['vester_hassing', 'Vester Hassing', 57.07, 10.13, 'DK1', true],
  // Vendsyssel og Himmerland (bane 5)
  ['hjoerring', 'Hjørring', 57.46, 9.98, 'DK1', false],
  ['frederikshavn', 'Frederikshavn', 57.44, 10.54, 'DK1', true],
  ['broenderslev', 'Brønderslev', 57.27, 9.94, 'DK1', false],
  ['dronninglund', 'Dronninglund', 57.16, 10.29, 'DK1', false],
  ['hobro', 'Hobro', 56.64, 9.79, 'DK1', true],
  ['noerrekaer', 'Nørrekær Enge', 56.95, 9.27, 'DK1', true],
  ['aars', 'Aars', 56.8, 9.52, 'DK1', false],
  ['blokhus', 'Blokhus', 57.25, 9.58, 'DK1', true],
  ['jammerbugt', 'Jammerbugt', 57.34, 9.47, 'DK1', false, { offshore: true }],
  // Vestjylland (bane 1)
  ['holstebro', 'Holstebro', 56.36, 8.62, 'DK1', false],
  ['struer', 'Struer', 56.49, 8.59, 'DK1', true],
  ['idomlund', 'Idomlund', 56.36, 8.44, 'DK1', false],
  ['lemvig', 'Lemvig', 56.55, 8.31, 'DK1', true],
  ['skive', 'Skive', 56.57, 9.03, 'DK1', true],
  ['herning', 'Herning', 56.14, 8.97, 'DK1', false],
  ['vinderup', 'Vinderup', 56.48, 8.78, 'DK1', false],
  ['hjerm', 'Hjerm', 56.39, 8.8, 'DK1', false],
  ['hovsoere', 'Høvsøre', 56.44, 8.15, 'DK1', true],
  ['nissum', 'Nissum Bredning', 56.63, 8.4, 'DK1', true],
  ['bovling', 'Bøvlingbjerg', 56.42, 8.25, 'DK1', false],
  ['thyboron', 'Thyborøn', 56.7, 8.21, 'DK1', true],
  ['videbaek', 'Videbæk', 56.09, 8.63, 'DK1', false],
  // Vestkysten (bane 2)
  ['thorsminde', 'Thorsminde', 56.37, 8.12, 'DK1', true],
  ['ringkobing', 'Ringkøbing', 56.09, 8.24, 'DK1', true],
  ['hvide_sande', 'Hvide Sande', 56.0, 8.17, 'DK1', true],
  ['skjern', 'Skjern', 55.95, 8.5, 'DK1', false],
  ['blaavand', 'Blåvand', 55.56, 8.08, 'DK1', true],
  ['varde', 'Varde', 55.62, 8.48, 'DK1', false],
  ['ribe', 'Ribe', 55.33, 8.76, 'DK1', true],
  ['grindsted', 'Grindsted', 55.76, 8.92, 'DK1', false],
  ['esbjerg', 'Esbjerg', 55.47, 8.45, 'DK1', true],
  ['esbjergvaerket', 'Esbjergværket', 55.44, 8.42, 'DK1', true],
  ['tjaereborg', 'Tjæreborg', 55.46, 8.58, 'DK1', false],
  ['endrup', 'Endrup', 55.52, 8.72, 'DK1', false],
  ['revsing', 'Revsing', 55.5, 9.13, 'DK1', false],
  ['thor', 'Thor', 56.35, 7.68, 'DK1', false, { offshore: true }],
  ['horns_rev', 'Horns Rev', 55.53, 7.9, 'DK1', false, { offshore: true }],
  // Midt- og Østjylland
  ['viborg', 'Viborg', 56.45, 9.4, 'DK1', false],
  ['tjele', 'Tjele', 56.47, 9.68, 'DK1', false],
  ['foulum', 'Foulum', 56.5, 9.57, 'DK1', false],
  ['trige', 'Trige', 56.25, 10.15, 'DK1', false],
  ['aarhus', 'Aarhus', 56.16, 10.2, 'DK1', true],
  ['malling', 'Malling', 56.04, 10.2, 'DK1', true],
  ['vejle', 'Vejle', 55.71, 9.54, 'DK1', true],
  ['fredericia', 'Fredericia', 55.57, 9.75, 'DK1', true],
  ['erritso', 'Erritsø', 55.53, 9.7, 'DK1', true],
  // Trekantområdet (bane 3)
  ['skaerbaek', 'Skærbæk', 55.51, 9.62, 'DK1', true],
  ['kolding', 'Kolding', 55.49, 9.47, 'DK1', true],
  ['horsens', 'Horsens', 55.86, 9.85, 'DK1', true],
  ['skanderborg', 'Skanderborg', 56.04, 9.93, 'DK1', false],
  ['hedensted', 'Hedensted', 55.77, 9.7, 'DK1', false],
  ['give', 'Give', 55.84, 9.24, 'DK1', false],
  ['juelsminde', 'Juelsminde', 55.71, 10.02, 'DK1', true],
  ['studstrup', 'Studstrup', 56.25, 10.34, 'DK1', true],
  ['braedstrup', 'Brædstrup', 55.97, 9.61, 'DK1', false],
  ['egtved', 'Egtved', 55.62, 9.31, 'DK1', false],
  // Midtjylland (bane 4)
  ['silkeborg', 'Silkeborg', 56.17, 9.55, 'DK1', false],
  ['randers', 'Randers', 56.46, 10.04, 'DK1', true],
  ['bjerringbro', 'Bjerringbro', 56.38, 9.66, 'DK1', false],
  ['ulstrup', 'Ulstrup', 56.39, 9.79, 'DK1', false],
  ['hammel', 'Hammel', 56.25, 9.86, 'DK1', false],
  ['hinnerup', 'Hinnerup', 56.27, 10.06, 'DK1', false],
  ['karup', 'Karup', 56.31, 9.17, 'DK1', false],
  ['thorning', 'Thorning', 56.26, 9.43, 'DK1', false],
  ['hvam', 'Hvam', 56.62, 9.48, 'DK1', false],
  ['rodkaersbro', 'Rødkærsbro', 56.36, 9.5, 'DK1', false],
  ['laurbjerg', 'Laurbjerg', 56.43, 9.94, 'DK1', false],
  ['ikast', 'Ikast', 56.14, 9.16, 'DK1', false],
  ['ry', 'Ry', 56.09, 9.82, 'DK1', false],
  // Sønderjylland
  ['kasso', 'Kassø', 55.03, 9.3, 'DK1', false],
  ['aabenraa', 'Aabenraa', 55.04, 9.42, 'DK1', true],
  // Fyn
  ['odense', 'Odense', 55.4, 10.39, 'DK1', false],
  ['fraugde', 'Fraugde', 55.36, 10.48, 'DK1', true],
  // Fyn og Vestsjælland omkring Storebælt (bane 6)
  ['fynsvaerket', 'Fynsværket', 55.47, 10.38, 'DK1', true],
  ['nyborg', 'Nyborg', 55.31, 10.79, 'DK1', true],
  ['svendborg', 'Svendborg', 55.06, 10.61, 'DK1', true],
  ['ringe', 'Ringe', 55.24, 10.48, 'DK1', false],
  ['middelfart', 'Middelfart', 55.5, 9.73, 'DK1', true],
  ['sprogoe', 'Sprogø', 55.33, 10.97, 'DK2', false, { offshore: true }],
  ['korsoer', 'Korsør', 55.33, 11.14, 'DK2', true],
  ['slagelse', 'Slagelse', 55.4, 11.35, 'DK2', false],
  ['holbaek', 'Holbæk', 55.72, 11.72, 'DK2', true],
  ['ringsted', 'Ringsted', 55.44, 11.79, 'DK2', false],
  ['naestved', 'Næstved', 55.23, 11.76, 'DK2', true],
  // Sjælland, Lolland-Falster og Bornholm (DK2)
  ['herslev', 'Herslev', 55.53, 11.25, 'DK2', true],
  ['asnaes', 'Asnæs', 55.66, 11.08, 'DK2', true],
  ['bjaeverskov', 'Bjæverskov', 55.45, 12.03, 'DK2', false],
  ['ishoj', 'Ishøj', 55.62, 12.35, 'DK2', true],
  ['hovegaard', 'Hovegård', 55.8, 12.4, 'DK2', false],
  ['kobenhavn', 'København', 55.68, 12.57, 'DK2', true],
  ['lolland', 'Lolland', 54.75, 11.4, 'DK2', true],
  ['bornholm', 'Bornholm', 55.1, 14.9, 'DK2', true],
  ['kriegers_flak', 'Kriegers Flak', 55.05, 12.95, 'DK2', false, { offshore: true }],
  // Udland i kortkanten
  ['norge', 'Norge', 57.9, 8.3, 'DK1', false, { foreign: true, green: 1 }],
  ['sverige_vest', 'Sverige', 57.4, 11.6, 'DK1', false, { foreign: true, green: 1 }],
  ['tyskland_vest', 'Tyskland', 54.6, 9.3, 'DK1', false, { foreign: true, green: 0.5 }],
  ['uk', 'Storbritannien', 55.6, 6.4, 'DK1', false, { foreign: true, green: 0.5 }],
  ['holland', 'Holland', 54.8, 7.2, 'DK1', false, { foreign: true, green: 0.5 }],
  ['sverige_syd', 'Sverige', 55.9, 13.0, 'DK2', false, { foreign: true, green: 1 }],
  ['tyskland_ost', 'Tyskland', 54.4, 12.3, 'DK2', false, { foreign: true, green: 0.5 }],
];

export const MAP_NODES: readonly MapNode[] = RAW.map(([id, name, lat, lon, area, coast, extra]) => ({
  id,
  name,
  lat,
  lon,
  ...project(lat, lon),
  area,
  coast,
  ...(extra ?? {}),
}));

const BY_ID = new Map(MAP_NODES.map((n) => [n.id, n]));

export function mapNode(id: string): MapNode {
  const n = BY_ID.get(id);
  if (!n) throw new Error(`Ukendt kortnode: ${id}`);
  return n;
}

/** Udlandsforbindelser og Storebælt (spilværdier i MW). */
export const FIXED_LINKS: readonly { a: string; b: string; capMw: number; name: string }[] = [
  { a: 'norge', b: 'tjele', capMw: 1700, name: 'Skagerrak' },
  { a: 'sverige_vest', b: 'vester_hassing', capMw: 740, name: 'Konti-Skan' },
  { a: 'sverige_syd', b: 'hovegaard', capMw: 1700, name: 'Øresund' },
  { a: 'tyskland_vest', b: 'kasso', capMw: 2500, name: 'Kassø-Tyskland' },
  { a: 'tyskland_ost', b: 'bjaeverskov', capMw: 600, name: 'Kontek' },
  { a: 'tyskland_ost', b: 'kriegers_flak', capMw: 400, name: 'Kriegers Flak' },
  { a: 'uk', b: 'revsing', capMw: 1400, name: 'Viking Link' },
  { a: 'holland', b: 'endrup', capMw: 700, name: 'COBRA' },
  { a: 'fraugde', b: 'herslev', capMw: 600, name: 'Storebælt' },
];

// --- Kystlinje (grov, lat/lon), projiceret ved indlæsning --------------------------------------------

type LatLon = [number, number];

const JYLLAND: LatLon[] = [
  [57.75, 10.6], [57.58, 10.43], [57.45, 10.55], [57.25, 10.5], [57.1, 10.3], [56.98, 10.3],
  [56.8, 10.3], [56.62, 10.4], [56.5, 10.9], [56.4, 10.95], [56.2, 10.75], [56.15, 10.25],
  [55.95, 10.25], [55.85, 10.05], [55.7, 9.75], [55.55, 9.75], [55.45, 9.55], [55.25, 9.5],
  [55.05, 9.45], [54.9, 9.6], [54.8, 9.45], [54.85, 8.65], [55.1, 8.55], [55.35, 8.6],
  [55.47, 8.4], [55.56, 8.08], [55.8, 8.15], [56.0, 8.12], [56.15, 8.1], [56.4, 8.12],
  [56.6, 8.18], [56.75, 8.25], [56.95, 8.4], [57.1, 8.6], [57.12, 8.95], [57.15, 9.35],
  [57.4, 9.85], [57.6, 10.15],
];

const FYN: LatLon[] = [
  [55.56, 9.9], [55.6, 10.2], [55.62, 10.55], [55.45, 10.8], [55.25, 10.8], [55.05, 10.6],
  [55.1, 10.2], [55.25, 9.95], [55.45, 9.8],
];

const SJAELLAND: LatLon[] = [
  [56.1, 12.3], [56.03, 12.62], [55.7, 12.6], [55.5, 12.25], [55.35, 12.4], [55.18, 12.2],
  [55.05, 11.95], [55.15, 11.7], [55.2, 11.25], [55.4, 11.12], [55.6, 11.1], [55.75, 11.0],
  [55.9, 11.35], [55.95, 11.7], [55.97, 11.95], [56.05, 12.05],
];

const LOLLAND_FALSTER: LatLon[] = [
  [54.95, 11.1], [54.8, 11.95], [54.57, 11.95], [54.65, 11.45], [54.7, 11.05], [54.85, 10.98],
];

const BORNHOLM: LatLon[] = [
  [55.3, 14.78], [55.2, 15.15], [55.05, 15.05], [54.99, 14.7], [55.15, 14.7],
];

function poly(pts: LatLon[]): { x: number; y: number }[] {
  return pts.map(([lat, lon]) => project(lat, lon));
}

/** Landmasser som lukkede polygoner i km. */
export const COASTLINE: readonly { name: string; points: { x: number; y: number }[] }[] = [
  { name: 'Jylland', points: poly(JYLLAND) },
  { name: 'Fyn', points: poly(FYN) },
  { name: 'Sjælland', points: poly(SJAELLAND) },
  { name: 'Lolland-Falster', points: poly(LOLLAND_FALSTER) },
  { name: 'Bornholm', points: poly(BORNHOLM) },
];
